import {
    AfterContentChecked,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ComponentRef,
    Input,
    OnDestroy,
    Renderer2,
    ViewChild,
    ViewContainerRef,
    inject
} from "@angular/core";
import { IMapComponent } from "../../../map.models";
import { IMapGeometry } from "../../../map.settings";
import { Position } from "./map-popup.service";

@Component({
    selector: "rask-map-popup",
    templateUrl: "./map-popup.component.html",
    styleUrls: ["./map-popup.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MapPopupComponent implements OnDestroy, AfterContentChecked {
    @ViewChild("arrow", { read: ViewContainerRef,
        static: false }) arrowContainer: ViewContainerRef;
    @Input() innerComponent: any;
    innerComponentRef: ComponentRef<any>;
    @ViewChild("innerContainer", { read: ViewContainerRef,
        static: false }) innerContainer: ViewContainerRef;
    @Input() innerData: any;
    target?: IMapGeometry;
    viewContainer: ViewContainerRef;
    @Input() onSizeChanged: () => void;
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly ref = inject(ViewContainerRef);
    private readonly renderer = inject(Renderer2);

    private map: IMapComponent;
    private popupHeight: number;
    private popupWidth: number;

    constructor() {
        const ref = this.ref;

        this.viewContainer = ref;
    }

    activate() {
        this.inflateInnerComponent();
    }

    checkPopupSize() {
        const width = this.viewContainer.element.nativeElement.clientWidth;
        const height = this.viewContainer.element.nativeElement.clientHeight;
        if (this.onSizeChanged && (width !== this.popupWidth || height !== this.popupHeight)) {
            this.onSizeChanged();
            this.popupWidth = width;
            this.popupHeight = height;
        }
    }

    deactivate() {
        this.deflateInnerComponent();
    }

    deflateInnerComponent() {
        this.renderer.removeClass(this.ref.element.nativeElement, "open");
        this.ngOnDestroy();
        this.innerComponent = undefined;
        this.innerData = undefined;
    }

    drawArrow(posClass: string) {
        this.renderer.addClass(this.arrowContainer.element.nativeElement, posClass);
    }

    inflateInnerComponent() {
        this.innerComponentRef = this.innerContainer.createComponent(this.innerComponent);
        this.innerComponentRef.instance.data = this.innerData;
        this.innerComponentRef.instance.map = this.map;
        this.changeDetectorRef.detectChanges();
    }

    ngAfterContentChecked(): void {
        this.checkPopupSize();
    }

    ngOnDestroy(): void {
        if (this.innerComponentRef) {
            this.innerComponentRef.destroy();
        }
    }

    resetArrow() {
        this.renderer.removeClass(this.arrowContainer.element.nativeElement, "top");
        this.renderer.removeClass(this.arrowContainer.element.nativeElement, "topLeft");
        this.renderer.removeClass(this.arrowContainer.element.nativeElement, "bottomLeft");
        this.renderer.removeClass(this.arrowContainer.element.nativeElement, "bottom");
        this.renderer.removeClass(this.arrowContainer.element.nativeElement, "topRight");
        this.renderer.removeClass(this.arrowContainer.element.nativeElement, "bottomRight");
    }

    setInnerComponent(component: any, data: any, map: IMapComponent) {
        this.deactivate();
        this.innerComponent = component;
        this.innerData = data;
        this.map = map;
        this.activate();
    }

    setOnCloseHandler(closeHandler: (result: any) => void) {
        this.innerComponentRef.instance.onClose = (result) => {
            closeHandler(result);
            if (this.innerComponent) {
                this.deflateInnerComponent();
            }
        };
    }

    setPosition(x, y, position: Position) {
        this.resetArrow();
        switch (position) {
            case Position.BottomRight:
                this.renderer.setStyle(this.ref.element.nativeElement, "top", `${y - 21}px`);
                this.renderer.setStyle(this.ref.element.nativeElement, "left", `${x + 12}px`);
                this.drawArrow("bottomRight");
                break;
            case Position.TopRight:
                this.renderer.setStyle(this.ref.element.nativeElement, "top", `${y - (this.ref.element.nativeElement.clientHeight - 21)}px`);
                this.renderer.setStyle(this.ref.element.nativeElement, "left", `${x + 12}px`);
                this.drawArrow("topRight");
                break;
            case Position.Bottom:
                this.renderer.setStyle(this.ref.element.nativeElement, "top", `${y + 12}px`);
                this.renderer.setStyle(this.ref.element.nativeElement, "left", `${x - (this.ref.element.nativeElement.clientWidth / 2)}px`);
                this.drawArrow("bottom");
                break;
            case Position.Top:
                this.renderer.setStyle(this.ref.element.nativeElement, "top", `${y - (this.ref.element.nativeElement.clientHeight + 12)}px`);
                this.renderer.setStyle(this.ref.element.nativeElement, "left", `${x - (this.ref.element.nativeElement.clientWidth / 2)}px`);
                this.drawArrow("top");
                break;
            case Position.BottomLeft:
                this.renderer.setStyle(this.ref.element.nativeElement, "top", `${y - (this.ref.element.nativeElement.clientHeight - 21)}px`);
                this.renderer.setStyle(this.ref.element.nativeElement, "left", `${x - (this.ref.element.nativeElement.clientWidth + 12)}px`);
                this.drawArrow("bottomLeft");
                break;
            case Position.TopLeft:
                this.renderer.setStyle(this.ref.element.nativeElement, "top", `${y - 21}px`);
                this.renderer.setStyle(this.ref.element.nativeElement, "left", `${x - (this.ref.element.nativeElement.clientWidth + 12)}px`);
                this.drawArrow("topLeft");
                break;
            default:
                break;
        }
    }

    show() {
        this.renderer.addClass(this.ref.element.nativeElement, "open");
        this.changeDetectorRef.detectChanges();
    }
}
